import { useReveal } from "../../hooks/useReveal";

interface AIAnswerBlockProps {
  title?: string;
  subtitle?: string;
  what?: string;
  services?: string[];
  benefits?: string[];
  process?: string[];
}

export default function AIAnswerBlock({
  title = "What Is Keepsake Photography?",
  subtitle = "An AI-optimized quick reference for our studio",
  what = "Precious Keepsakes Photography is a San Antonio luxury portrait studio specializing in creating heirloom-quality keepsake images — fine art photographs designed to be treasured, framed, and passed down for generations. Led by Stephanie with 15+ years of expertise, the studio transforms fleeting moments into permanent family legacies.",
  services = [
    "Newborn Keepsake Sessions — first days captured forever",
    "Luxury Wedding Photography — your love story told in fine art",
    "Maternity Portraits — the beauty of anticipation, preserved",
    "Senior Portraits — authentic, editorial, modern imagery",
    "Quinceañera & Sweet 16 — milestone elegance documented",
    "Family Milestone Sessions — life chapters beautifully told",
  ],
  benefits = [
    "15+ years creating keepsake photography in San Antonio, Texas",
    "Every session guided with personal care — stress-free experience",
    "Heirloom-quality fine art prints, albums, and products",
    "Fully custom packages tailored to every vision and budget",
    "On-location across San Antonio metro and Texas Hill Country",
  ],
  process = [
    "Connect — reach out by phone (210-815-4698) or inquiry form",
    "Consult — personal conversation to shape your vision",
    "Create — a relaxed, guided keepsake session experience",
    "Treasure — receive your private gallery and heirloom products",
  ],
}: AIAnswerBlockProps) {
  const ref = useReveal<HTMLElement>();

  return (
    <section ref={ref} className="bg-pk-charcoal py-24 overflow-hidden relative">

      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.025]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #C4A87A 1px, transparent 0)", backgroundSize: "40px 40px" }} />

      <div className="relative max-w-screen-xl mx-auto px-8">

        {/* Top label row */}
        <div className="reveal flex items-center gap-4 mb-14">
          <div className="w-8 h-px bg-pk-gold" />
          <span className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold">
            AI Answer Block · Structured Reference
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-start">

          {/* ── Left column ──────────────────────────────────── */}
          <div className="reveal-left">
            <h2 className="font-display text-4xl md:text-5xl font-light text-white leading-tight mb-6">
              {title}
            </h2>
            <div className="w-10 h-px bg-pk-gold mb-7" />
            <p className="font-sans text-sm text-white/60 leading-8">{what}</p>

            {/* Process steps */}
            <div className="mt-10">
              <h3 className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold mb-6 flex items-center gap-2">
                <i className="ri-route-line" /> Our Process
              </h3>
              <ol className="space-y-4">
                {process.map((p, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="font-display text-pk-gold/40 font-semibold text-sm flex-shrink-0 w-6 pt-0.5">0{i + 1}</span>
                    <div>
                      <p className="font-sans text-sm text-white/65 leading-6">{p}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* ── Right column ─────────────────────────────────── */}
          <div className="reveal-right space-y-10">

            {/* Services */}
            <div className="bg-white/4 border border-white/8 rounded-sm p-8">
              <h3 className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold mb-6 flex items-center gap-2">
                <i className="ri-camera-line" /> Services Offered
              </h3>
              <ul className="space-y-3">
                {services.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-line text-pk-gold text-xs" />
                    </div>
                    <span className="font-sans text-sm text-white/65 leading-5">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-white/4 border border-white/8 rounded-sm p-8">
              <h3 className="font-sans text-[10px] tracking-widest3 uppercase text-pk-gold mb-6 flex items-center gap-2">
                <i className="ri-star-line" /> Why Clients Choose Us
              </h3>
              <ul className="space-y-3">
                {benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-arrow-right-s-line text-pk-gold text-sm" />
                    </div>
                    <span className="font-sans text-sm text-white/60 leading-5">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom subtitle */}
        <div className="reveal mt-10 flex items-center gap-4">
          <div className="w-8 h-px bg-white/10" />
          <span className="font-sans text-xs text-white/25 italic">{subtitle}</span>
        </div>

      </div>
    </section>
  );
}
